import InformationTip from '@/components/InformationTip';
import { Button } from '@/components/buttons';
import { Input } from '@/components/inputs';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { DataSource, DataSourceCodeConfig, DataSourceType, DataSourceTypePluginConfig } from '@/models/dataSource';
import { RootState } from '@/reducers';
import { DocumentDuplicateIcon, DownloadIcon, ExternalLinkIcon } from '@heroicons/react/outline';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

type WordPressInstallPluginStepProps = {
  dataSourceType: DataSourceType;
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
};

const DEFAULT_WORDPRESS_REPOSITORY_URL = 'https://wordpress.org/plugins/';
const DEFAULT_WORDPRESS_ZIP_URL = 'https://download.audiencerate.dev/ar-tracker.zip';

const StepSection: FC<{
  badge: string;
  title: string;
  children: ReactNode;
}> = ({ badge, title, children }) => {
  return (
    <section className='overflow-hidden rounded-lg border border-gray-200 shadow-sm'>
      <div className='flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4'>
        <span className='grid size-7 shrink-0 place-items-center rounded-full bg-brand-600 text-sm font-semibold text-white-100'>{badge}</span>
        <h2 className='text-base font-semibold text-gray-900'>{title}</h2>
      </div>
      <div className='p-5'>{children}</div>
    </section>
  );
};

const CredentialField: FC<{
  label: string;
  tooltipContent: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
}> = ({ label, tooltipContent, value, onCopy, copied }) => {
  const { t } = useTranslation();

  return (
    <div className='grid gap-3 md:grid-cols-[10rem,minmax(0,1fr),auto] md:items-end'>
      <div className='flex items-center gap-2 pb-2'>
        <label className='text-sm font-medium text-gray-700'>{label}</label>
        <InformationTip tooltipContent={tooltipContent} className='size-4 text-gray-400' />
      </div>
      <Input value={value} isViewMode className='min-h-11 rounded-md bg-gray-50 text-sm text-gray-700' />
      <Button variant='primary' className='h-11 gap-2 px-4 text-sm' onClick={onCopy}>
        <DocumentDuplicateIcon className='size-5' />
        {copied ? String(t('data_source_edit:plugin.install_plugin.copied')) : String(t('COMMON:copy' as any))}
      </Button>
    </div>
  );
};

export const WordPressInstallPluginStep: FC<WordPressInstallPluginStepProps> = ({ dataSourceType, onNextStep, onPrevStep }) => {
  const { t } = useTranslation();
  const workspace = useWorkspace();
  const [copiedField, setCopiedField] = useState<'workspaceId' | 'dataSourceId' | null>(null);

  const dataSource = useSelector((state: RootState) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCodeConfig>> | undefined;

  const pluginConfiguration = (dataSourceType.configuration ?? {}) as DataSourceTypePluginConfig;

  const repositoryUrl = pluginConfiguration.pluginRepositoryUrl ?? pluginConfiguration.website ?? DEFAULT_WORDPRESS_REPOSITORY_URL;
  const zipUrl = pluginConfiguration.pluginZipUrl ?? DEFAULT_WORDPRESS_ZIP_URL;
  const zipVersion = pluginConfiguration.pluginZipVersion;

  const copyToClipboard = async (value: string, field: 'workspaceId' | 'dataSourceId') => {
    if (!value) return;

    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    window.setTimeout(() => {
      setCopiedField((current) => (current === field ? null : current));
    }, 1500);
  };

  const handleOpenLink = (url: string) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className='flex flex-col gap-6'>
      <div className='space-y-2'>
        <h1 className='text-xl font-semibold text-gray-900'>{String(t('data_source_edit:plugin.install_plugin.title'))}</h1>
        <p className='text-sm text-gray-600'>{String(t('data_source_edit:plugin.install_plugin.subtitle'))}</p>
      </div>

      <StepSection badge='a' title={String(t('data_source_edit:plugin.install_plugin.download_section_title'))}>
        <div className='grid gap-4 lg:grid-cols-2'>
          <div className='rounded-lg border border-gray-200 p-5'>
            <div className='space-y-3'>
              <h3 className='text-base font-semibold text-gray-900'>{String(t('data_source_edit:plugin.install_plugin.repository_title'))}</h3>
              <p className='text-sm leading-6 text-gray-600'>{String(t('data_source_edit:plugin.install_plugin.repository_description'))}</p>
              <Button variant='light' className='h-11 gap-2 px-4 text-sm' onClick={() => handleOpenLink(repositoryUrl)} disabled={!repositoryUrl}>
                {String(t('data_source_edit:plugin.install_plugin.repository_cta'))}
                <ExternalLinkIcon className='size-5' />
              </Button>
            </div>
          </div>

          <div className='rounded-lg border border-gray-200 p-5'>
            <div className='space-y-3'>
              <h3 className='text-base font-semibold text-gray-900'>{String(t('data_source_edit:plugin.install_plugin.zip_title'))}</h3>
              <p className='text-sm leading-6 text-gray-600'>{String(t('data_source_edit:plugin.install_plugin.zip_description'))}</p>
              <Button variant='primary' className='h-11 gap-2 px-4 text-sm' onClick={() => handleOpenLink(zipUrl)} disabled={!zipUrl}>
                <DownloadIcon className='size-5' />
                {String(
                  t('data_source_edit:plugin.install_plugin.zip_cta', {
                    version: zipVersion ?? String(t('data_source_edit:plugin.install_plugin.latest_version')),
                  })
                )}
              </Button>
            </div>
          </div>
        </div>
      </StepSection>

      <StepSection badge='b' title={String(t('data_source_edit:plugin.install_plugin.credentials_section_title'))}>
        <div className='space-y-5'>
          <p className='text-sm leading-6 text-gray-600'>{String(t('data_source_edit:plugin.install_plugin.credentials_description'))}</p>

          <CredentialField
            label={String(t('data_source_edit:plugin.install_plugin.workspace_id'))}
            tooltipContent={String(t('data_source_edit:plugin.install_plugin.workspace_id_tooltip'))}
            value={workspace.id}
            onCopy={() => copyToClipboard(workspace.id, 'workspaceId')}
            copied={copiedField === 'workspaceId'}
          />

          <CredentialField
            label={String(t('data_source_edit:plugin.install_plugin.data_source_id'))}
            tooltipContent={String(t('data_source_edit:plugin.install_plugin.data_source_id_tooltip'))}
            value={dataSource?.id ?? ''}
            onCopy={() => copyToClipboard(dataSource?.id ?? '', 'dataSourceId')}
            copied={copiedField === 'dataSourceId'}
          />
        </div>
      </StepSection>

      <StepSection badge='c' title={String(t('data_source_edit:plugin.install_plugin.paste_section_title'))}>
        <p className='text-sm leading-6 text-gray-600'>{String(t('data_source_edit:plugin.install_plugin.paste_section_description'))}</p>
      </StepSection>

      <StepPageActions
        primaryButton={{
          text: 'COMMON:confirm',
          onClick: onNextStep,
        }}
        secondaryButton={{
          text: 'COMMON:cancel',
          onClick: onPrevStep,
        }}
      />
    </div>
  );
};
